import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Plus,
  Minus,
  Calculator,
  Save,
  X,
  AlertTriangle,
  Camera,
  ChevronDown,
} from "lucide-react";
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, getAuthors, storage } from "../../services/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { MarkdownField } from "../ui/MarkDown";
import { query, where, getDocs, Timestamp } from "firebase/firestore";
import { validateImageFile } from "../utils/imageValidation";
import { resizeImage } from "../utils/resizeImage";
import { cropImageFile } from "../utils/cropImage";
import Cropper from "react-easy-crop";
import { AuthorType } from "../../types/blog";
import AddBlogAuthorModal from "../Blog/AddBlogAuthorModal";
import { getEasternDateTime } from "../Blog/Blog";

// Types and interfaces
interface Stake {
  title: string;
  description: string;
}

interface PlayerStat {
  position: string;
  positionRank: number;
  statId: string;
}

interface FormulaStep {
  type: "stat" | "operator" | "number" | "text";
  value: string | number;
  playerStat?: PlayerStat;
}

interface FormulaGroup {
  name: string;
  description: string;
  steps: FormulaStep[];
}

interface WeekOption {
  value: number;
  label: string;
  status: "upcoming" | "current" | "completed";
}

interface ChallengeCalculatorProps {
  challengeId?: string;
  onSaveSuccess?: () => void;
  setIsEdit?: (value: boolean) => void;
  isEdit?: boolean;
}

// Available stats from Yahoo API
export const availableStats = [
  // QB total points
  { id: "qb_pts", name: "QB Fantasy Points", category: "QB Total" },

  // RB total points
  { id: "rb_pts", name: "RB Fantasy Points", category: "RB Total" },

  // WR total points
  { id: "wr_pts", name: "WR Fantasy Points", category: "WR Total" },

  // TE total points
  { id: "te_pts", name: "TE Fantasy Points", category: "TE Total" },

  // K total points
  { id: "k_pts", name: "K Fantasy Points", category: "K Total" },

  // DEF total points
  { id: "def_pts", name: "DEF Fantasy Points", category: "DEF Total" },

  // Existing stats
  { id: "4", name: "Passing Yards", category: "Passing" },
  { id: "5", name: "Passing TDs", category: "Passing" },
  { id: "6", name: "Interceptions", category: "Passing" },
  { id: "9", name: "Rushing Yards", category: "Rushing" },
  { id: "10", name: "Rushing TDs", category: "Rushing" },
  { id: "11", name: "Receptions", category: "Receiving" },
  { id: "12", name: "Receiving Yards", category: "Receiving" },
  { id: "13", name: "Receiving TDs", category: "Receiving" },
  { id: "18", name: "Fumbles Lost", category: "Misc" },

  // Defense stats
  { id: "32", name: "Sacks", category: "Defense" },
  { id: "33", name: "Interceptions", category: "Defense" },
  { id: "34", name: "Fumble Recoveries", category: "Defense" },
  { id: "35", name: "Defensive TDs", category: "Defense" },
  { id: "36", name: "Safeties", category: "Defense" },
  { id: "37", name: "Blocked Kicks", category: "Defense" },
  { id: "38", name: "Points Allowed", category: "Defense" },

  // Kicker stats
  { id: "19", name: "FG Made 0-19", category: "Kicking" },
  { id: "20", name: "FG Made 20-29", category: "Kicking" },
  { id: "21", name: "FG Made 30-39", category: "Kicking" },
  { id: "22", name: "FG Made 40-49", category: "Kicking" },
  { id: "23", name: "FG Made 50+", category: "Kicking" },
  { id: "fg_total", name: "Total FG Made", category: "Kicking" },
  { id: "29", name: "PAT Made", category: "Kicking" },
  { id: "30", name: "PAT Missed", category: "Kicking" },
  {
    id: "FG_TOTAL_MISSED",
    name: "Total Field Goals Missed",
    category: "Kicking",
  },
];

const operators = [
  { value: "+", label: "Add (+)" },
  { value: "-", label: "Subtract (-)" },
  { value: "*", label: "Multiply (×)" },
  { value: "/", label: "Divide (÷)" },
];

const basePositions = ["QB", "RB", "WR", "TE", "K", "DEF"];

const getAvailablePositions = () => {
  const positions: { value: string; label: string }[] = [];
  basePositions.forEach((pos) => {
    if (pos === "QB") {
      positions.push({
        value: "QB1",
        label: "QB1",
      });
    } else if (pos === "TE") {
      for (let i = 1; i <= 2; i++) {
        positions.push({
          value: `TE${i}`,
          label: `TE${i}`,
        });
      }
    } else {
      for (let i = 1; i <= 3; i++) {
        positions.push({
          value: `${pos}${i}`,
          label: `${pos}${i}`,
        });
      }
    }
  });

  // Add K and DEF positions with explicit numbers
  positions.push({ value: "K1", label: "K" });
  positions.push({ value: "DEF1", label: "DEF" });

  return positions;
};

const ChallengeCalculator: React.FC<ChallengeCalculatorProps> = ({
  challengeId,
  onSaveSuccess,
  setIsEdit,
  isEdit,
}) => {
  // State management
  const currentYear = new Date().getFullYear();
  const years = [currentYear + 1, currentYear, currentYear - 1];
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedWeek, setSelectedWeek] = useState<number>(0);
  const [selectedYear, setSelectedYear] = useState<string>(
    currentYear.toString()
  );
  const [stakes, setStakes] = useState<Stake[]>([
    { title: "", description: "" },
  ]);
  const [formulaGroups, setFormulaGroups] = useState<FormulaGroup[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [currentNflWeek, setCurrentNflWeek] = useState<number>(1);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [slug, setSlug] = useState("");
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<any>(null);
  const [crop, setCrop] = useState({ x: 16, y: 9 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  const [loadingCropper, setLoadingCropper] = useState(false);
  const [selectedAspectRatioType, setSelectedAspectRatioType] =
    useState("landscape");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [authors, setAuthors] = useState<AuthorType[]>([]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedAuthorId, setSelectedAuthorId] = useState("");
  const [dateTimeInEasternTime, setDateTimeInEasternTime] = useState<
    string | number | readonly string[] | undefined
  >(getEasternDateTime());

  const [imageMaxHeight, setImageMaxHeight] = useState<number | null | undefined>(null);

  useEffect(() => {
    const fetchExistingChallenge = async () => {
      if (!challengeId) return;

      try {
        const challengeDoc = await getDoc(
          doc(db, "customChallenges", challengeId)
        );
        if (challengeDoc.exists()) {
          const data = challengeDoc.data();
          setSelectedImage(data.image);
          setImagePreview(data.image);
          setTitle(data.title || "");
          setSubtitle(data.subtitle || "");
          setDescription(data.description || "");
          setSelectedWeek(data.week || 0);
          setStakes(data.stakes || [{ title: "", description: "" }]);
          setFormulaGroups(data.formulaGroups || []);
          setSlug(data.slug || "");
          setSelectedAuthorId(data.authorId || "");
          setImageMaxHeight(data.imageMaxHeight??null);
          setDateTimeInEasternTime(
            data.dateTimeInEasternTime || getEasternDateTime()
          );
          if (data.year) {
            setSelectedYear(data.year);
          } else {
            setSelectedYear(
              new Date(data.createdAt.toDate()).getFullYear().toString()
            );
          }
        }
      } catch (error) {
        console.error("Error fetching challenge:", error);
        setError("Failed to load challenge data");
      }
    };

    const fetchCurrentWeek = async () => {
      try {
        const weekDoc = await getDoc(doc(db, "currentWeek", "nfl"));
        if (weekDoc.exists()) {
          setCurrentNflWeek(weekDoc.data().week || 1);
        }
      } catch (error) {
        console.error("Error fetching current week:", error);
      }
    };

    fetchCurrentWeek();
    if (challengeId) {
      fetchExistingChallenge();
    }
  }, [challengeId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Formula Group Management
  const handleAddFormulaGroup = () => {
    setFormulaGroups([
      ...formulaGroups,
      {
        name: `Formula ${formulaGroups.length + 1}`,
        description: "",
        steps: [],
      },
    ]);
  };

  const handleAddFormulaStep = (
    groupIndex: number,
    type: FormulaStep["type"]
  ) => {
    const newGroups = [...formulaGroups];
    const newStep: FormulaStep =
      type === "number" ? { type: "number", value: 0 } : type === "operator" ? { type: "operator", value: "+" } : type === "text" ? { type: "text" , value: ""}
        : {
            type: "stat",
            value: "",
            playerStat: { position: "", positionRank: 1, statId: "" },
          };

    newGroups[groupIndex].steps.push(newStep);
    setFormulaGroups(newGroups);
  };

  const handleRemoveFormulaStep = (groupIndex: number, stepIndex: number) => {
    const newGroups = [...formulaGroups];
    newGroups[groupIndex].steps.splice(stepIndex, 1);
    setFormulaGroups(newGroups);
  };

  const handleUpdateFormulaStep = (
    groupIndex: number,
    stepIndex: number,
    field: string,
    value: string | number
  ) => {
    const newGroups = [...formulaGroups];
    const step = newGroups[groupIndex].steps[stepIndex];

    if (step.type === "stat" && step.playerStat) {
      if (field === "position") {
        // Special handling for K and DEF
        if (value === "K" || value === "DEF") {
          step.playerStat.position = value;
          step.playerStat.positionRank = 1; // Default to 1 for these positions
        } else {
          // Original logic for other positions
          const [basePos, rank] = (value as string)
            .match(/([A-Z]+)(\d*)/)
            ?.slice(1) || ["", ""];
          step.playerStat.position = basePos;
          step.playerStat.positionRank = parseInt(rank) || 1;
        }
      } else if (field === "statId") {
        step.playerStat.statId = value as string;
      }
    } else {
      step.value = value;
    }

    setFormulaGroups(newGroups);
  };

  // Stakes Management
  const handleAddStake = () => {
    setStakes([...stakes, { title: "", description: "" }]);
  };

  const handleRemoveStake = (index: number) => {
    setStakes(stakes.filter((_, i) => i !== index));
  };

  const handleStakeChange = (
    index: number,
    field: keyof Stake,
    value: string
  ) => {
    const newStakes = [...stakes];
    newStakes[index][field] = value;
    setStakes(newStakes);
  };

  // Validation
  const validateChallenge = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return false;
    }

    if (!description.trim()) {
      setError("Description is required");
      return false;
    }

    if (selectedWeek === 0) {
      setError("Please select a week");
      return false;
    }

    if (
      stakes.length === 0 ||
      !stakes.some((stake) => stake.title.trim() && stake.description.trim())
    ) {
      setError("At least one complete stake is required");
      return false;
    }

    if (formulaGroups.length === 0) {
      setError("At least one formula is required");
      return false;
    }

    if (!slug.trim()) {
      setError("Slug is required");
      return false;
    }

    if (slug.trim() !== "") {
      const checkDuplicate: any = query(
        collection(db, "customChallenges"),
        where("slug", "==", slug)
      );

      const conflictsSnapshot = await getDocs(checkDuplicate);

      // Get all documents' data as an array of objects
      const conflicts = conflictsSnapshot.docs.map((doc) => ({
        id: doc.id,
      }));
      if (!challengeId) {
        if (conflicts.length > 0) {
          setError("Slug is already taken");
          return false;
        }
      } else {
        if (conflicts.length > 0 && conflicts[0]["id"] !== challengeId) {
          setError("Slug is already taken");
          return false;
        }
      }
    }

    // Validate formula groups
    for (const group of formulaGroups) {
      if (!group.name.trim()) {
        setError("All formulas must have a name");
        return false;
      }

      if (group.steps.length === 0) {
        setError(`Formula "${group.name}" has no steps`);
        return false;
      }

      // Check for invalid step sequences
      let lastStepType = "";
      for (const step of group.steps) {
        if (step.type === "operator" && lastStepType === "operator") {
          setError(`Formula "${group.name}" has consecutive operators`);
          return false;
        }
        if (step.type === "stat" && !step.playerStat?.statId) {
          setError(`Formula "${group.name}" has incomplete stat selection`);
          return false;
        }
        lastStepType = step.type;
      }

      // Check first and last steps aren't operators
      if (
        group.steps[0].type === "operator" ||
        group.steps[group.steps.length - 1].type === "operator"
      ) {
        setError(
          `Formula "${group.name}" cannot start or end with an operator`
        );
        return false;
      }
    }

    setError(null);
    return true;
  };

  // const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (file) {
  //     setSelectedImage(file);
  //     const previewUrl = URL.createObjectURL(file);
  //     setImagePreview(previewUrl);
  //   }
  // };
  const resetImageState = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (fileInputRef.current) fileInputRef.current.value = "";

      const validationError = validateImageFile(file);
      if (validationError) {
        alert(validationError);
        setError(validationError);
        return;
      }

      setLoadingCropper(true);
      try {
        const { resizedUrl: resized } = await resizeImage(file);
        setTempImageUrl(resized);
        setShowCropper(true);
        setError(null);
      } finally {
        setLoadingCropper(false);
      }
    },
    []
  );

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!tempImageUrl || !croppedArea) return;

    const cropped = await cropImageFile(tempImageUrl, croppedArea);
    if (cropped) {
      setSelectedImage(cropped);
      setImagePreview(URL.createObjectURL(cropped));
    }

    setShowCropper(false);
  }, [tempImageUrl, croppedArea]);

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const generateSlug = (title: string) => {
    let slug = "";
    if (title) {
      slug = title
        .normalize("NFD") // split accents from letters
        .replace(/[\u0300-\u036f]/g, "") // remove accent marks
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-") // collapse non-alphanumerics to hyphens
        .replace(/^-+|-+$/g, ""); // remove leading/trailing hyphens
    }
    setSlug(slug);
  };

  // Save functionality
  const handleSave = async () => {
    if (!(await validateChallenge())) {
      return;
    }

    setIsSaving(true);
    try {
      // Upload image and get URL if image exists
      let imageUrl = null;
      if (selectedImage && selectedImage.name) {
        const storageRef = ref(
          storage,
          `custom-challenges/${Date.now()}_${selectedImage.name}`
        );
        await uploadBytes(storageRef, selectedImage);
        imageUrl = await getDownloadURL(storageRef);
      } else {
        imageUrl = selectedImage ? selectedImage : null;
      }

      // If this is a new challenge (no challengeId), find and update the current active challenge
      if (!challengeId) {
        const activeChallengesQuery = query(
          collection(db, "customChallenges"),
          where("status", "==", "active")
        );

        const activeSnapshots = await getDocs(activeChallengesQuery);

        // Update all active challenges to completed
        const updatePromises = activeSnapshots.docs.map((doc) =>
          updateDoc(doc.ref, {
            status: "completed",
            completedAt: serverTimestamp(),
          })
        );

        await Promise.all(updatePromises);
      }

      let selectedAuthorName = authors.find(
        (a) => a.id === selectedAuthorId
      )?.name;
      let selectedAuthorThumbnail =
        authors.find((a) => a.id === selectedAuthorId)?.thumbnail ||
        `https://ui-avatars.com/api/?name=${encodeURIComponent(
          authors.find((a) => a.id === selectedAuthorId)?.name || "Author"
        )}&background=random&size=32`;

      const challengeData = {
        title: title.trim(),
        subtitle: subtitle.trim(),
        description: description.trim(),
        week: selectedWeek,
        stakes: stakes.filter(
          (stake) => stake.title.trim() && stake.description.trim()
        ),
        formulaGroups: formulaGroups.map((group) => ({
          ...group,
          name: group.name.trim(),
          description: group.description.trim(),
          steps: group.steps.map((step) => ({
            ...step,
            value: step.type === "number" ? Number(step.value) : step.value,
          })),
        })),
        image: imageUrl,
        status: "active",
        slug: slug,
        updatedAt: serverTimestamp(),
        year: selectedYear,
        authorId: selectedAuthorId || '',
        author: selectedAuthorName || '',
        thumbnail: selectedAuthorThumbnail || '',
        dateTimeInEasternTime: dateTimeInEasternTime,
        imageMaxHeight: imageMaxHeight ? imageMaxHeight : null
      };

      if (challengeId) {
        console.log("update", challengeData);
        await updateDoc(
          doc(db, "customChallenges", challengeId),
          challengeData
        );
        isEdit ? setIsEdit?.(false) : setIsEdit?.(true);
      } else {
        await addDoc(collection(db, "customChallenges"), {
          ...challengeData,
          createdAt: serverTimestamp(),
        });
      }

      // Clear form and image state after successful save
      if (!challengeId) {
        setTitle("");
        setSubtitle("");
        setDescription("");
        setSelectedWeek(0);
        setStakes([{ title: "", description: "" }]);
        setFormulaGroups([]);
        setSelectedImage(null);
        setImagePreview(null);
        setDateTimeInEasternTime(getEasternDateTime());
        setSlug("");
        setImageMaxHeight(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }

      if (onSaveSuccess) {
        onSaveSuccess();
      }

      setError(null);
    } catch (error) {
      console.error("Error saving challenge:", error);
      setError("Failed to save challenge. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const groupedStats = availableStats.reduce((acc, stat) => {
    if (!acc[stat.category]) {
      acc[stat.category] = [];
    }
    acc[stat.category].push(stat);
    return acc;
  }, {} as Record<string, typeof availableStats>);

  const imageSection = useMemo(
    () => (
      <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md">
        <div className="space-y-2 text-center">
          <div className="flex flex-col items-center">
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-48 rounded-lg"
                />
                <button
                  type="button"
                  onClick={resetImageState}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <Camera size={48} className="mx-auto text-gray-400" />
            )}
          </div>
          <label
            htmlFor="image-author-upload"
            className="cursor-pointer text-[#18f7b1] hover:text-[#0fd494]"
          >
            {imagePreview ? "Change image" : "Upload an image"}
            <input
              id="image-author-upload"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="sr-only"
            />
          </label>
          <p className="text-sm text-gray-500">
            PNG, JPG, JPEG, WEBP up to 10MB
          </p>
        </div>
      </div>
    ),
    [imagePreview, handleImageChange, resetImageState]
  );

  const handleAspectRatioChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAspectRatioType(e.target.value);
    const aspectType = e.target.value;
    if (aspectType === "portrait") {
      setCrop({ x: 9, y: 16 });
    } else if (aspectType === "landscape") {
      setCrop({ x: 16, y: 9 });
    }
    resetImageState();
  };

  const getAspectRatio = () => {
    if (selectedAspectRatioType === "portrait") {
      return 9 / 16;
    } else if (selectedAspectRatioType === "landscape") {
      return 16 / 9;
    } else if (selectedAspectRatioType === "square") {
      return 1;
    }
  };

  const fetchAuthors = async () => {
    try {
      const authorsList = await getAuthors();
      setAuthors(authorsList);
    } catch (error) {
      console.error("Failed to fetch authors:", error);
    }
  };

  useEffect(() => {
    fetchAuthors();
  }, []);

  const authorOptions = useMemo(() => {
    return authors.map((author) => ({
      id: author.id,
      name: author.name,
      image: author.image,
      thumbnail: author.thumbnail,
    }));
  }, [authors]);

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Challenge Info Section */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h4 className="bottomGap0">Challenge Information</h4>
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <AlertTriangle className="text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                generateSlug(e.target.value);
              }}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
              placeholder="Enter title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                generateSlug(e.target.value);
              }}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
              placeholder="Enter slug"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
            >
              {years.map((year) => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Challenge Image
            </label>
            <select
              value={selectedAspectRatioType}
              onChange={handleAspectRatioChange}
              defaultValue={selectedAspectRatioType}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
            >
              <option value="landscape">Landscape (16:9)</option>
              <option value="portrait">Portrait (9:16)</option>
              <option value="square">Square (1:1)</option>
            </select>
            {imageSection}
          </div>
          {loadingCropper && (
            <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
              <div className="text-white text-xl flex items-center gap-4">
                <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-white border-opacity-70" />
                Preparing image for cropping...
              </div>
            </div>
          )}
          {showCropper && tempImageUrl && (
            <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
              <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
                <Cropper
                  image={tempImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={getAspectRatio()}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
                <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
                  <button
                    onClick={() => setShowCropper(false)}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropConfirm}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Crop & Save
                  </button>
                </div>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-2">
              Challenge Image Max Height (Leave blank to load default)
            </label>
            <input
              type="number"
              value={imageMaxHeight || ''}
              onChange={(e) => {
                setImageMaxHeight(parseInt(e.target.value));
              }}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
              placeholder="250"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Week</label>
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(Number(e.target.value))}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
            >
              <option value={0}>Select Week</option>
              {Array.from({ length: 18 }, (_, i) => {
                const weekNum = i + 1;
                let status =
                  weekNum < currentNflWeek
                    ? "completed"
                    : weekNum === currentNflWeek
                    ? "current"
                    : "upcoming";
                return (
                  <option
                    key={weekNum}
                    value={weekNum}
                    className={
                      status === "current"
                        ? "font-bold"
                        : status === "completed"
                        ? "text-gray-500"
                        : ""
                    }
                  >
                    Week {weekNum} {status === "current" ? "(Current)" : ""}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium mb-2">Author</label>

            <div className="flex">
              <button
                onClick={() => setDropdownOpen((prev) => !prev)}
                type="button"
                className="w-full flex items-center justify-between p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1] bg-white"
              >
                {selectedAuthorId ? (
                  <div className="flex items-center gap-2">
                    <img
                      src={
                        authors.find((a) => a.id === selectedAuthorId)
                          ?.thumbnail ||
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          authors.find((a) => a.id === selectedAuthorId)
                            ?.name || "Author"
                        )}&background=random&size=32`
                      }
                      alt="Selected Author"
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span>
                      {authors.find((a) => a.id === selectedAuthorId)?.name ||
                        "Select Author"}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-500">Select an author</span>
                )}
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
              {/* <AddBlogAuthorModal isAdmin={true} onAuthorAdded={fetchAuthors} /> */}
            </div>
            {dropdownOpen && (
              <ul className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-md max-h-60 overflow-auto">
                {authorOptions.map((author) => (
                  <li
                    key={author.id}
                    onClick={() => {
                      setSelectedAuthorId(author.id);
                      setDropdownOpen(false);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                      selectedAuthorId === author.id ? "bg-gray-100" : ""
                    }`}
                  >
                    <img
                      loading="lazy"
                      src={
                        author.thumbnail ||
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          author.name
                        )}&background=random&size=32`
                      }
                      alt={author.name}
                      className="w-6 h-6 rounded-full object-cover"
                      onError={(e) =>
                        (e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          author.name
                        )}&background=random&size=32`)
                      }
                    />
                    <span>{author.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {/* Blog Date Time block */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Date and Time (EST/EDT)
            </label>
            <input
              type="datetime-local"
              value={dateTimeInEasternTime}
              onChange={(e) => {
                setDateTimeInEasternTime(e.target.value);
              }}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Subtitle</label>
          <MarkdownField
            value={subtitle}
            onChange={(value) => setSubtitle(value || "")}
            placeholder="Optional subtitle"
            minHeight={100}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Description</label>
          <MarkdownField
            value={description}
            onChange={(value) => setDescription(value || "")}
            placeholder="Describe how the challenge works and what players need to do"
            minHeight={200}
          />
        </div>
      </div>

      {/* Stakes Section */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="bottomGap0">Stakes</h4>
          <button
            onClick={handleAddStake}
            className="p-2 bg-[#18f7b1] text-black rounded-full hover:bg-[#0fd494] transition-colors"
            title="Add new stake"
          >
            <Plus size={20} />
          </button>
        </div>

        {stakes.map((stake, index) => (
          <div key={index} className="p-4 border rounded-lg space-y-3">
            <div className="flex gap-4">
              <div className="flex-grow space-y-3">
                <MarkdownField
                  value={stake.title}
                  onChange={(value) =>
                    handleStakeChange(index, "title", value || "")
                  }
                  placeholder="Stake title (e.g., 'Winner's Prize')"
                  minHeight={100}
                />
                <MarkdownField
                  value={stake.description}
                  onChange={(value) =>
                    handleStakeChange(index, "description", value || "")
                  }
                  placeholder="Describe what happens to the winner/loser"
                  minHeight={150}
                />
              </div>
              {stakes.length > 1 && (
                <button
                  onClick={() => handleRemoveStake(index)}
                  className="text-red-500 hover:text-red-700 p-2"
                  title="Remove stake"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Formula Builder Section */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="bottomGap0">Formula Builder</h4>
          <button
            onClick={handleAddFormulaGroup}
            className="p-2 bg-[#18f7b1] text-black rounded-full hover:bg-[#0fd494] transition-colors"
            title="Add new formula"
          >
            <Calculator size={20} />
          </button>
        </div>

        {formulaGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="p-4 border rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <input
                type="text"
                value={group.name}
                onChange={(e) => {
                  const newGroups = [...formulaGroups];
                  newGroups[groupIndex].name = e.target.value;
                  setFormulaGroups(newGroups);
                }}
                className="text-xl font-bold bg-transparent border-b-2 border-transparent focus:border-[#18f7b1] focus:outline-none"
                placeholder="Formula Name"
              />

              <div className="flex gap-2">
                <button
                  onClick={() => handleAddFormulaStep(groupIndex, "text")}
                  className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
                  title="Add player stat"
                >
                  + Text
                </button>
                <button
                  onClick={() => handleAddFormulaStep(groupIndex, "stat")}
                  className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
                  title="Add player stat"
                >
                  + Stat
                </button>
                <button
                  onClick={() => handleAddFormulaStep(groupIndex, "operator")}
                  className="px-3 py-1 bg-green-500 text-white rounded-md text-sm hover:bg-green-600"
                  title="Add operator"
                >
                  + Operator
                </button>
                <button
                  onClick={() => handleAddFormulaStep(groupIndex, "number")}
                  className="px-3 py-1 bg-purple-500 text-white rounded-md text-sm hover:bg-purple-600"
                  title="Add number"
                >
                  + Number
                </button>
                <button
                  onClick={() => {
                    const newGroups = [...formulaGroups];
                    newGroups.splice(groupIndex, 1);
                    setFormulaGroups(newGroups);
                  }}
                  className="p-1 text-red-500 hover:text-red-700 rounded-full"
                  title="Remove formula"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center min-h-[50px] p-2 bg-gray-50 rounded-lg">
              {group.steps.map((step, stepIndex) => (
                <div
                  key={stepIndex}
                  className="flex items-center gap-2 bg-white p-2 rounded-md shadow-sm"
                >
                  {step.type === "stat" ? (
                    <div className="flex gap-2">
                      <select
                        value={`${step.playerStat?.position}${step.playerStat?.positionRank}`}
                        onChange={(e) =>
                          handleUpdateFormulaStep(
                            groupIndex,
                            stepIndex,
                            "position",
                            e.target.value
                          )
                        }
                        className="p-2 border rounded-md bg-white"
                      >
                        <option value="">Position</option>
                        {getAvailablePositions().map((pos) => (
                          <option key={pos.value} value={pos.value}>
                            {pos.label}
                          </option>
                        ))}
                      </select>

                      <select
                        value={step.playerStat?.statId || ""}
                        onChange={(e) =>
                          handleUpdateFormulaStep(
                            groupIndex,
                            stepIndex,
                            "statId",
                            e.target.value
                          )
                        }
                        className="p-2 border rounded-md bg-white"
                      >
                        <option value="">Select Stat</option>
                        {availableStats.map((stat) => (
                          <option key={stat.id} value={stat.id}>
                            {stat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : step.type === "operator" ? (
                    <select
                      value={step.value}
                      onChange={(e) =>
                        handleUpdateFormulaStep(
                          groupIndex,
                          stepIndex,
                          "value",
                          e.target.value
                        )
                      }
                      className="p-2 border rounded-md w-32 bg-white"
                    >
                      {operators.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                    ) : step.type === "text" ? (
                        <input 
                          type="text" 
                          value={step.value} 
                          onChange={(e) =>
                            handleUpdateFormulaStep(
                              groupIndex,
                              stepIndex,
                              "value",
                              e.target.value
                            )
                          }
                          className="p-2 border rounded-md w-96"
                          placeholder="Enter text"
                        />
                    ) : (
                    <input
                      type="number"
                      value={step.value}
                      onChange={(e) =>
                        handleUpdateFormulaStep(
                          groupIndex,
                          stepIndex,
                          "value",
                          parseFloat(e.target.value)
                        )
                      }
                      className="p-2 border rounded-md w-32"
                      placeholder="Enter number"
                    />
                  )}

                  <button
                    onClick={() =>
                      handleRemoveFormulaStep(groupIndex, stepIndex)
                    }
                    className="text-red-500 hover:text-red-700"
                    title="Remove step"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              {group.steps.length === 0 && (
                <div className="text-gray-400 italic">
                  Add steps to build your formula
                </div>
              )}
            </div>
          </div>
        ))}

        {formulaGroups.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            Click the calculator icon above to add your first formula
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`
            px-6 py-3 bg-[#18f7b1] text-black rounded-lg hover:bg-[#0fd494] 
            flex items-center gap-2 transition-all duration-200
            ${isSaving ? "opacity-50 cursor-not-allowed" : ""}
          `}
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
              Saving...
            </>
          ) : (
            <>
              <Save size={20} />
              {challengeId ? "Update" : "Save"}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ChallengeCalculator;
